import { useState } from "react"

import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { deleteClientProtected } from "../../clients/clients"
import { TTR_DELETE_COMMENT, LCT_DELETE_COMMENT } from "../../urls/private"

interface IDeleteCommentHook {
    isSending: boolean
    error: IAppError | undefined
    postResponse: (
        deviceSystemRef: string,
        comment: string,
        dataset?: Datasets
    ) => Promise<void>
}

/**
 * Provides functions and logic for posting a delete comment request
 * on a TTR device.
 * @returns {IDeleteCommentHook} - loading and error states.
 */
const useDeleteComment = (): IDeleteCommentHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const postResponse = async (
        deviceSystemRef: string,
        commentId: string,
        dataset?: Datasets
    ): Promise<void> => {
        try {
            setIsSending(true)

            // Build response
            if (
                dataset &&
                ![Datasets.TTR, Datasets.TTR_DRAFT].includes(dataset)
            ) {
                await deleteClientProtected(
                    LCT_DELETE_COMMENT(deviceSystemRef, commentId, dataset)
                )
            } else {
                await deleteClientProtected(
                    TTR_DELETE_COMMENT(deviceSystemRef, commentId)
                )
            }
            setError(undefined)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        postResponse,
    }
}

export default useDeleteComment
